<i18n>
{
  "de": {
    "targetPath": "Zielpfad",
    "allBuildings": "Gesamtportfolio",
    "selectedBuildings": "Auswahl ({n} Liegenschaften)",
    "activeBuildings": "{n} aktive Liegenschaft | {n} aktive Liegenschaften",
    "crremApartments": "Zielpfad CRREM 2023 (Mehrfamilienhaus)",
    "crremOffices": "Zielpfad CRREM 2023 (Büro)",
    "bafu2022": "Zielpfad BAFU PACTA 2022",
    "bafu2024": "Zielpfad BAFU PACTA 2024",
    "strandingEvent": "Stranding-Event",
    "multipleStrandingEvents": "(Mehrere Zielpfade)",
    "scope": {
      "s1e": "Scope 1 Emissionen",
      "s2e": "Scope 2 Emissionen",
      "s3e": "Scope 3 Emissionen"
    },
    "yLabels": {
      "absolute": {
        "ee": "Endenergie (MWh)",
        "rh": "Raumwärmebedarf (MWh)",
        "s12e": "Scope 1-2 Emissionen (t CO₂e)",
        "s123e": "Scope 1-3 Emissionen (t CO₂e)",
        "per": "Primärenergie (erneuerbar) (MWh)"
      },
      "relative": {
        "ee": "Endenergie (kWh/m²)",
        "rh": "Raumwärmebedarf (kWh/m²)",
        "s12e": "Scope 1-2 Emissionen (kg CO₂e/m²)",
        "s123e": "Scope 1-3 Emissionen (kg CO₂e/m²)",
        "per": "Primärenergie (erneuerbar) (kWh/m²)"
      }
    },
    "units": {
      "absolute": {
        "ee": "MWh",
        "rh": "MWh",
        "s12e": "t CO₂e",
        "s123e": "t CO₂e",
        "per": "MWh"
      },
      "relative": {
        "ee": "kWh/m²",
        "rh": "kWh/m²",
        "s12e": "kg CO₂e/m²",
        "s123e": "kg CO₂e/m²",
        "per": "kWh/m²"
      }
    },
    "absoluteValues": "Absolut",
    "relativeValues": "Spezifisch"
  }
}
</i18n>

<template>
  <AsyncContentContainer :content-state="contentState" class="kpi-compare-selection-chart">
    <template #default>
      <FaultsContainer :faults="projections?.all?.[kpiType]?.faulty" :portfolio="portfolio" />
      <div class="justify-right">
        <ToggleButton :options="unitsModeOptions" :value="unitsMode" @change="setUnitsMode" />
      </div>
      <LineChart ref="lineChart" :chart-data="chartData.data" :options="chartData.options" exportable />
    </template>
  </AsyncContentContainer>
</template>

<script>
import _ from 'lodash'

import { mapGetters } from 'vuex'

import AsyncContentMixin from '@/components/shared/AsyncContentMixin.vue'
import ChartOptionsMixin from '@/pages/vuex-mixins/ChartOptionsMixin.vue'

import AsyncContentContainer from '@/components/shared/AsyncContentContainer.vue'
import FaultsContainer from '@/components/report/charts/FaultsContainer.vue'
import LineChart from '@/components/shared/charts/LineChart.vue'
import ToggleButton from '@/components/cui/inputs/ToggleButton.vue'
import colors from '@/services/color-palettes.js'

import compassApi from '@/services/compass-api.js'

import crremApartmentsStaticData from '@/data/CRREM_2023_Apartments_StaticData.json'
import crremOfficesStaticData from '@/data/CRREM_2023_Offices_StaticData.json'
import bafu2022StaticData from '@/data/BAFU_PACTA_2022_StaticData.json'
import bafu2024StaticData from '@/data/BAFU_PACTA_2024_StaticData.json'

export default {
  mixins: [
    //
    AsyncContentMixin,
    ChartOptionsMixin,
  ],

  components: {
    LineChart,
    AsyncContentContainer,
    FaultsContainer,
    ToggleButton,
  },

  props: {
    portfolio: {
      type: Object,
      required: true,
    },
    query: {
      type: Object,
    },
    scenario: {
      type: Object,
      required: true,
    },
    kpiType: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      projections: {},
      roundPrecision: 1,
      unitsMode: 'absolute', // 'absolute' or 'relative'
    }
  },

  computed: {
    ...mapGetters({
      __getEnhancedSustainabilityIndicator: 'portfolio/getEnhancedSustainabilityIndicatorByIdentifier',
    }),

    unitsModeOptions() {
      return [
        { label: this.$t('absoluteValues'), value: 'absolute' },
        { label: this.$t('relativeValues'), value: 'relative' },
      ]
    },

    targets() {
      const targets = this.portfolio.targets
      const kpiTypes = ['s12e', 's123e', 'ee', 'pe']

      if (!targets || !kpiTypes.includes(this.kpiType)) {
        return []
      }

      // Filter the portfolio targets for the kpiType (e.g. s12e_total) and non-null values (empty values are null, but there can be valid 0 values)
      const filteredTargets = targets.filter((x) => x[`${this.kpiType}_total`] !== null)

      if (filteredTargets.length === 0) {
        return []
      }

      // Get min and max years from filtered targets
      const years = filteredTargets.map((x) => x.year)
      const minYear = Math.min(...years)
      const maxYear = Math.max(...years)

      // Create array of all years between min and max
      const allYears = []
      for (let year = minYear; year <= maxYear; year++) {
        allYears.push(year)
      }

      // Compute values for all years in the range
      return allYears
        .map((year) => {
          // Find if there's a target for this specific year
          const target = filteredTargets.find((x) => x.year === year)

          if (target) {
            // If target exists for this year, use it
            const yearIdx = this.projections.all[this.kpiType].years.indexOf(year)
            const area = this.projections.all[this.kpiType].projections[this.scenario.id].area[yearIdx]

            return {
              year: year,
              value:
                this.unitsMode === 'relative'
                  ? target[`${this.kpiType}_total`]
                  : (target[`${this.kpiType}_total`] * area) / 1000,
            }
          } else {
            // If no target for this year, interpolate between nearest targets
            const prevTarget = filteredTargets.filter((x) => x.year < year).sort((a, b) => b.year - a.year)[0]

            const nextTarget = filteredTargets.filter((x) => x.year > year).sort((a, b) => a.year - b.year)[0]

            if (prevTarget && nextTarget) {
              // Linear interpolation between two targets
              const prevYearIdx = this.projections.all[this.kpiType].years.indexOf(prevTarget.year)
              const nextYearIdx = this.projections.all[this.kpiType].years.indexOf(nextTarget.year)

              // Interpolate the target value
              let prevValue, nextValue

              if (this.unitsMode === 'relative') {
                prevValue = prevTarget[`${this.kpiType}_total`]
                nextValue = nextTarget[`${this.kpiType}_total`]
              } else {
                const prevArea = this.projections.all[this.kpiType].projections[this.scenario.id].area[prevYearIdx]
                const nextArea = this.projections.all[this.kpiType].projections[this.scenario.id].area[nextYearIdx]

                prevValue = (prevTarget[`${this.kpiType}_total`] * prevArea) / 1000
                nextValue = (nextTarget[`${this.kpiType}_total`] * nextArea) / 1000
              }

              const interpolationFactor = (year - prevTarget.year) / (nextTarget.year - prevTarget.year)
              const interpolatedValue = prevValue + (nextValue - prevValue) * interpolationFactor

              return {
                year: year,
                value: interpolatedValue,
              }
            } else if (prevTarget) {
              // Extrapolate from previous target (use same value)
              if (this.unitsMode === 'relative') {
                return {
                  year: year,
                  value: prevTarget[`${this.kpiType}_total`],
                }
              } else {
                const yearIdx = this.projections.all[this.kpiType].years.indexOf(year)
                const area = this.projections.all[this.kpiType].projections[this.scenario.id].area[yearIdx]

                return {
                  year: year,
                  value: (prevTarget[`${this.kpiType}_total`] * area) / 1000,
                }
              }
            } else if (nextTarget) {
              // Extrapolate from next target (use same value)
              if (this.unitsMode === 'relative') {
                return {
                  year: year,
                  value: nextTarget[`${this.kpiType}_total`],
                }
              } else {
                const yearIdx = this.projections.all[this.kpiType].years.indexOf(year)
                const area = this.projections.all[this.kpiType].projections[this.scenario.id].area[yearIdx]

                return {
                  year: year,
                  value: (nextTarget[`${this.kpiType}_total`] * area) / 1000,
                }
              }
            }
          }

          // Fallback: return null if no interpolation/extrapolation possible
          return {
            year: year,
            value: null,
          }
        })
        .filter((x) => x.value !== null) // Remove any null values
    },

    sustainabilityIndicator() {
      return this.__getEnhancedSustainabilityIndicator(this.kpiType)
    },

    yLabel() {
      if (this.getIndicatorName === this.kpiType) {
        return this.$t(`yLabels.${this.unitsMode}.${this.kpiType}`)
      }
      return `${this.getIndicatorName} (${this.unit})`
    },

    getIndicatorName() {
      if (!this.sustainabilityIndicator) {
        return this.kpiType
      }
      return this.sustainabilityIndicator.name
    },

    unit() {
      if (this.sustainabilityIndicator === undefined) {
        return this.kpiType
      }
      if (this.sustainabilityIndicator === null) {
        return this.$t(`units.${this.unitsMode}.${this.kpiType}`)
      }
      return this.unitsMode === 'relative'
        ? this.sustainabilityIndicator.getSpecificTargetUnit()
        : this.sustainabilityIndicator.getBigTargetUnit()
    },

    options() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        /* aspectRatio: 3, */
        scales: {
          yAxis: {
            beginAtZero: true,
            // Warning: `stacked: true` causes issues with stranding events.
            // If there are multiple stranding events in the same year (rarely occurs),
            // the scatter plot points will get stacked on top of each other.
            // Meaning point 1 will be correctly displayed, but then point 2 will be 2x as high, etc.
            // There is a workaround below for this case.
            stacked: true,
            title: {
              display: true,
              text: this.yLabel,
            },
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: (item) => {
                if (item.dataset.stranding) {
                  return item.dataset.label
                }
                // Display the value and the number of active buildings
                // e.g. 100 kWh/m² (1000 active buildings)
                return `${this.getTooltipLabelWithReductionString(item, {
                  precision: this.roundPrecision,
                  unit: this.unit,
                })} (${this.$tc(
                  'activeBuildings',
                  this.projections.selection[this.kpiType].active_buildings_per_year[item.parsed.x],
                  {
                    n: this.projections.selection[this.kpiType].active_buildings_per_year[item.parsed.x],
                  }
                )})
                `
              },
            },
          },
          interaction: {
            mode: 'nearest',
          },
          legend: {
            position: 'bottom',
            align: 'start',
            display: true,
            reverse: true,
            onClick: (e, legendItem, legend) => {
              // Handle legend click for stranding events
              this.handleLegendClick(e, legendItem, legend)
            },
          },
          annotation: {
            annotations: {},
          },
        },
      }
    },

    chartData() {
      let datasets = []
      let reductionPathsData = {}

      // Add CRREM reduction paths for apartments and offices in Scope 1-3 emissions
      if (this.kpiType === 's123e') {
        reductionPathsData.crremApartments = this.getStaticData(crremApartmentsStaticData)
        datasets.push(
          this.getDataset(reductionPathsData.crremApartments, this.$t('crremApartments'), '#888888', {
            borderDash: [5, 5],
          })
        )
        reductionPathsData.crremOffices = this.getStaticData(crremOfficesStaticData)
        datasets.push(
          this.getDataset(reductionPathsData.crremOffices, this.$t('crremOffices'), '#bbbbbb', {
            borderDash: [8, 8],
          })
        )
      }

      // Add BAFU reduction path for Scope 1 emissions
      if (this.kpiType === 's1e') {
        reductionPathsData.bafu2024 = this.getStaticData(bafu2024StaticData)
        datasets.push(
          this.getDataset(reductionPathsData.bafu2024, this.$t('bafu2024'), '#888888', {
            borderDash: [5, 5],
          })
        )
        reductionPathsData.bafu2022 = this.getStaticData(bafu2022StaticData)
        datasets.push(
          this.getDataset(reductionPathsData.bafu2022, this.$t('bafu2022'), '#bbbbbb', {
            borderDash: [5, 5],
          })
        )
      }

      // Add user reduction path targets if available
      if (this.targets.length > 0) {
        reductionPathsData.targets = this.projections.all[this.kpiType].years
          .map((year) => this.targets.find((target) => target['year'] === year))
          .map((x) => (x === undefined ? null : x['value']))
        datasets.push(this.getDataset(reductionPathsData.targets, this.$t('targetPath'), 'rgba(0,0,0,0.1)', {}))
      }

      // Add all buildings in portfolio (if there is a filter applied)
      if (this.projections.selection[this.kpiType].nbuildings !== this.projections.all[this.kpiType].nbuildings) {
        reductionPathsData.buildings = this.projections.all[this.kpiType].projections[this.scenario.id].value.map(
          (v, i) =>
            this.unitsMode === 'relative'
              ? v / this.projections.all[this.kpiType].projections[this.scenario.id].area[i]
              : v / 1000
        )
        datasets.push(this.getDataset(reductionPathsData.buildings, this.$t('allBuildings'), 'rgba(50,0,200,0.3)', {}))
      }

      // Add selected buildings (all if no filter applied)
      reductionPathsData.buildings = this.projections.selection[this.kpiType].projections[this.scenario.id].value.map(
        (v, i) =>
          this.unitsMode === 'relative'
            ? v / this.projections.selection[this.kpiType].projections[this.scenario.id].area[i]
            : v / 1000
      )
      datasets.unshift(
        this.getDataset(
          reductionPathsData.buildings,
          this.projections.selection[this.kpiType].nbuildings !== this.projections.all[this.kpiType].nbuildings
            ? this.$t('selectedBuildings', { n: this.projections.selection[this.kpiType].nbuildings })
            : this.$t('allBuildings'),
          'rgba(50,0,200,0.9)',
          {}
        )
      )

      // s1e stacked area
      if (this.projections.selection.s1e && ['s12e', 's123e'].includes(this.kpiType)) {
        const scopeProjections = this.projections.selection.s1e.projections[this.scenario.id]
        const s1eBuildings = scopeProjections.value.map((v, i) =>
          this.unitsMode === 'relative' ? v / scopeProjections.area[i] : v / 1000
        )
        const datasetColors = colors.scopeAreaColors.s1e
        datasets.push(
          this.getScopeDataset(s1eBuildings, this.$t('scope.s1e'), datasetColors.background, datasetColors.border)
        )
      }

      // s2e stacked area
      if (this.projections.selection.s2e && ['s12e', 's123e'].includes(this.kpiType)) {
        const scopeProjections = this.projections.selection.s2e.projections[this.scenario.id]
        const s2eBuildings = scopeProjections.value.map((v, i) =>
          this.unitsMode === 'relative' ? v / scopeProjections.area[i] : v / 1000
        )
        const datasetColors = colors.scopeAreaColors.s2e
        datasets.push(
          this.getScopeDataset(s2eBuildings, this.$t('scope.s2e'), datasetColors.background, datasetColors.border)
        )
      }

      // s3e stacked area
      if (this.projections.selection.s3e && this.kpiType === 's123e') {
        const scopeProjections = this.projections.selection.s3e.projections[this.scenario.id]
        const s3eBuildings = scopeProjections.value.map((v, i) =>
          this.unitsMode === 'relative' ? v / scopeProjections.area[i] : v / 1000
        )
        const datasetColors = colors.scopeAreaColors.s3e
        datasets.push(
          this.getScopeDataset(s3eBuildings, this.$t('scope.s3e'), datasetColors.background, datasetColors.border)
        )
      }

      // Finding the first year where the reduction path is exceeded (stranding event)
      const years = this.projections.selection[this.kpiType].years
      const strandingYears = {
        crremApartments: null,
        crremOffices: null,
        bafu2024: null,
        bafu2022: null,
        targets: null,
      }

      for (let i = 0; i < reductionPathsData.buildings.length; i++) {
        // Get the stranding year for each reduction path
        _.keys(strandingYears).forEach((key) => {
          strandingYears[key] = this.getStrandingYear(reductionPathsData, strandingYears, key, years, i)
        })
      }

      let chartOptions = this.options

      // Workaround for avoiding stacking stranding events in the same year:
      // Detect duplicate years in strandingYears
      const duplicateYears = _.values(strandingYears)
        .filter((value) => value !== null)
        .filter((value, index, self) => self.indexOf(value) !== index)
      // Consolidate duplicate years into merged datasets.
      // Delete the keys for the duplicate years from strandingYears.
      // Add new keys to strandingYears for each duplicate year that
      // will be resolved in the translations as "(Mehrere Zielpfade)".
      if (duplicateYears.length > 0) {
        _.keys(strandingYears).forEach((key) => {
          if (duplicateYears.includes(strandingYears[key])) {
            delete strandingYears[key]
          }
        })
        duplicateYears.forEach((year) => {
          strandingYears[`multiple-${year}`] = year
          delete strandingYears[year]
        })
      }

      // Add scatter datasets for stranding years
      chartOptions.plugins.annotation.annotations = {}
      _.keys(strandingYears).forEach((key) => {
        if (strandingYears[key] !== null) {
          let label = ''
          let color = key.startsWith('multiple-') ? 'multiple' : key
          if (key.startsWith('multiple-')) {
            label = `${this.$t('strandingEvent')} ${this.$tc('multipleStrandingEvents', duplicateYears.length, {
              n: duplicateYears.length,
            })}`
          } else {
            label = `${this.$t('strandingEvent')}${
              key !== 'targets' ? ` ${this.$t(key)}` : ` ${this.$t('targetPath')}`
            }`
          }
          // Add stranding dataset
          datasets.push(
            this.getStrandingDataset(
              reductionPathsData.buildings,
              strandingYears[key],
              label,
              colors.strandingColors[color]
            )
          )
          const yearIndex = this.projections.all[this.kpiType].years.indexOf(strandingYears[key])
          chartOptions.plugins.annotation.annotations[key] = this.getStrandingAnnotation(
            yearIndex,
            colors.strandingColors[color]
          )
        }
      })

      return {
        data: {
          labels: this.projections.selection[this.kpiType].years,
          datasets: datasets,
        },
        options: chartOptions,
      }
    },
  },

  watch: {
    portfolio() {
      this.refreshContent()
    },
    scenario() {
      this.refreshContent()
    },
    query() {
      this.refreshContent()
    },
  },

  created() {
    const unitsMode = localStorage.getItem('chartUnitsMode')
    if (unitsMode) {
      this.unitsMode = unitsMode
      this.$store.dispatch('ui/setChartUnitsMode', unitsMode)
    } else {
      this.unitsMode = 'absolute'
      this.$store.dispatch('ui/setChartUnitsMode', 'absolute')
    }
  },

  methods: {
    async loadContentHandler(signal) {
      const query = JSON.stringify(this.query)
      const queryAll = JSON.stringify({})
      let projections = {
        selection: {},
        all: {},
      }
      // Projections for all buildings
      projections.all[this.kpiType] = await compassApi.call(
        `/projections/${this.portfolio.id}/${this.kpiType}/scenarios?query=${queryAll}`,
        'GET',
        null,
        signal
      )

      // Projections for selection
      projections.selection[this.kpiType] = await compassApi.call(
        `/projections/${this.portfolio.id}/${this.kpiType}/scenarios?query=${query}`,
        'GET',
        null,
        signal
      )

      if (['s12e', 's123e'].includes(this.kpiType)) {
        // Scope 1 projections
        projections.selection.s1e = await compassApi.call(
          `/projections/${this.portfolio.id}/s1e/scenarios?query=${query}`,
          'GET',
          null,
          signal
        )

        // Scope 2 projections
        projections.selection.s2e = await compassApi.call(
          `/projections/${this.portfolio.id}/s2e/scenarios?query=${query}`,
          'GET',
          null,
          signal
        )

        if (this.kpiType === 's123e') {
          // Scope 3 projections
          projections.selection.s3e = await compassApi.call(
            `/projections/${this.portfolio.id}/s3e/scenarios?query=${query}`,
            'GET',
            null,
            signal
          )
        }
      }
      this.projections = projections
    },

    getDataset(data, label, color, props) {
      return {
        stack: label,
        label: label,
        borderColor: color,
        pointBackgroundColor: '#ffffff',
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderWidth: 2,
        pointHoverRadius: 4,
        pointRadius: 2,
        borderWidth: 4,
        fill: false,
        lineTension: 0,
        spanGaps: true,
        pointHitRadius: 20,
        data: data,
        order: 3,
        ...props,
      }
    },

    getScopeDataset(data, label, color1, color2) {
      return {
        stack: 'stack',
        label: label,
        borderColor: color1,
        backgroundColor: color1,
        pointRadius: 2,
        pointBorderColor: color2,
        pointBackgroundColor: color1,
        borderWidth: 1,
        fill: true,
        lineTension: 0,
        spanGaps: true,
        data: data,
        order: 4,
      }
    },

    getStrandingYear(data, strandingYears, key, years, index) {
      if (data[key] && data[key][index] !== null && data.buildings[index] > data[key][index]) {
        if (strandingYears[key] === null) {
          return years[index]
        }
      }
      return strandingYears[key]
    },

    getStrandingDataset(data, year, label, color) {
      const strandingDataset = {
        label: label,
        type: 'scatter',
        data: [
          {
            x: year,
            y: data[this.projections.all[this.kpiType].years.indexOf(year)],
          },
        ],
        backgroundColor: color,
        borderColor: 'white',
        borderWidth: 1,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointStyle: 'circle',
        order: 1,
        stranding: true,
      }
      return strandingDataset
    },

    getStrandingAnnotation(yearIndex, color) {
      return {
        type: 'line',
        xMin: yearIndex,
        xMax: yearIndex,
        borderColor: color,
        borderWidth: 2,
        drawTime: 'afterDraw',
        order: 1,
      }
    },

    getStaticData(source) {
      return this.projections.all[this.kpiType].years.map((year, i) =>
        this.unitsMode === 'relative'
          ? source[year]
          : (source[year] * this.projections.all[this.kpiType].projections[this.scenario.id].area[i]) / 1000
      )
    },

    setUnitsMode(unitsMode) {
      this.unitsMode = unitsMode
      this.$store.dispatch('ui/setChartUnitsMode', unitsMode)
    },

    handleLegendClick(e, legendItem, legend) {
      const chart = legend.chart
      if (!chart) return

      const datasetIndex = legendItem.datasetIndex
      const dataset = chart.data.datasets[datasetIndex]

      if (dataset.stranding) {
        // Toggle dataset visibility
        const isVisible = !dataset.hidden
        dataset.hidden = isVisible

        // Find annotation by matching the label
        const label = dataset.label
        const key = this.findStrandingKeyByLabel(label)

        if (key && chart.options.plugins.annotation.annotations[key]) {
          chart.options.plugins.annotation.annotations[key].display = !isVisible
        }

        chart.update()
      } else {
        // Default behavior for non-stranding datasets
        const index = legendItem.datasetIndex
        const meta = chart.getDatasetMeta(index)
        meta.hidden = !meta.hidden
        chart.update()
      }
    },

    findStrandingKeyByLabel(label) {
      // Extract key from label like "Stranding-Event crremApartments"
      if (label.includes(this.$t('strandingEvent'))) {
        if (label.includes(this.$t('crremApartments'))) return 'crremApartments'
        if (label.includes(this.$t('crremOffices'))) return 'crremOffices'
        if (label.includes(this.$t('bafu2024'))) return 'bafu2024'
        if (label.includes(this.$t('bafu2022'))) return 'bafu2022'
        if (label.includes(this.$t('targetPath'))) return 'targets'
      }
      return null
    },
  },
}
</script>

<style lang="scss">
.kpi-compare-selection-chart {
  min-height: 250px;
  width: 100%;
  border: var(--box-border);
  border-radius: var(--box-radius);
  padding: var(--spacing-s);

  & .justify-right {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
